<template>
  <div>
    <va-modal
      :value="isOpen"
      ref="importRecordsModal"
      @ok="submit"
      @cancel="cancel"
      :okText="$t('modals.buttons.import')"
      :cancelText="$t('modals.buttons.cancel')"
      :okClass="'btn btn-primary btn-micro'"
      :cancelClass="'btn btn-secondary btn-micro'"
      :okToClose="false"
      :okDisabled="formHasErrors || isFetchingServer"
      size="small"
    >

      <!-- Modal Header -->
      <div slot="header" class="modal-title">
        <h4 class="mb-0">
          <b>{{ $t('ranu.records.import') }}</b>
        </h4>

        <!-- Entities Filter -->
        <div class="col-12" v-if="writeEntities.length > 1">
          <label class="form-label">{{ $t('users.inputs.entity') }}:</label>
          <div class="form-group" style="margin-bottom:1.25rem;">
            <div class="input-group clearable-filter">
              <multiselect
                v-model="form.entity"
                :options="writeEntities"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                :searchable="true"
                :allow-empty="false"
                :multiple="false">
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
                <small v-if="formErrors.has('entity_id')" class="help text-danger">
                  {{ $t(formErrors.get('entity_id')) }}
                </small>
              </multiselect>
            </div>
          </div>
        </div>
      </div>

      <div class="evollu-form mt-2">
        <div class="d-flex justify-content-center" v-if="!form.file">
          <input ref="importRecords" id="import-input" type="file" @input="onFileSelected" hidden
                 accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv">
          <a href="#" @click.prevent="uploadFile"><span class="fa fa-upload mr-1"></span>
            {{ $t('ranu.records.select_file_upload') }}</a>
        </div>
        <div class="d-flex justify-content-center align-items-center" v-else>
          <span class="font-weight-bold">{{ form.file.name }}</span>
          <i class="fa fa-times ml-1" @click.prevent="removeFile" style="margin-top:1px; color: #e34a4a;"></i>
        </div>
        <small v-if="formErrors.has('file')" class="help text-danger">
          {{ $t(formErrors.get('file')) }}
        </small>
        <small v-if="malformedExcelHeaderErrorFields" class="help text-danger">
          {{ malformedExcelHeaderErrorFields.length > 1 ? $t('ranu.records.errors.file_header_malformed_plural') : $t('ranu.records.errors.file_header_malformed')}}
          <ul id="excelHeaderErrors">
            <li v-for="item in malformedExcelHeaderErrorFields">
              -{{ item }};
            </li>
          </ul>
        </small>
        <small v-if="excelValidationError" class="help text-danger">
          {{ excelValidationError }}
        </small>
      </div>

      <progress-bar class="mb-2" v-if="isFetchingServer" :progress="uploadProgress"></progress-bar>

      <!--<div class="text-center pt-4">
        <small class="label clickable" @click.prevent="downloadTemplate">{{ $t('ranu.records.download_template')}}&ensp; <i class="fa fa-cloud-download"/> </small>
      </div>-->

    </va-modal>
  </div>
</template>
<script>
import ProgressBar from '@/components/common/UI/ProgressBar'
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/StandardErrors'

export default {
  components: {
    Multiselect,
    ProgressBar
  },
  data () {
    return {
      isOpen: false,
      malformedExcelHeaderErrorFields: null,
      excelValidationError: null,
      form: {
        entity: null,
        file: null
      },
      uploadProgress: 0,
      allowedExtensions: ['xlsx', 'xls', 'csv'],
      isFetchingServer: false,
      formErrors: new Errors()
    }
  },
  created () {
    if (this.writeEntities.length) {
      this.form.entity = this.writeEntities[0]
    }
  },
  mounted () {
    this.isOpen = true
  },
  methods: {
    downloadTemplate: function () {
      this.$http({
        url: `${this.$store.state.ranu_server}/v2/records/excel-template`,
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          if (response.data !== null && navigator.msSaveBlob) { return navigator.msSaveBlob(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'ranu_template') }
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
          link.download = 'ranu_template'
          document.body.appendChild(link)
          link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
          link.remove()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    removeFile: function () {
      this.form.file = ''
      this.formErrors.clear('file')
      this.malformedExcelHeaderErrorFields = null
      this.excelValidationError = null
    },
    onFileSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      this.form.file = files[0]
    },
    uploadFile: function () {
      this.$refs.importRecords.click()
    },
    submit: function (url = `${this.$store.state.ranu_server}/v2/records/import`) {
      this.formErrors.clear()
      this.isFetchingServer = true
      let formData = new FormData()
      formData.append('file', this.form.file)
      formData.append('entity_id', this.form.entity.id)
      this.$http.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: function (progressEvent) {
          this.uploadProgress = parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }.bind(this)
      }).then((response) => {
        let records = response.data.data
        this.$emit('import-success', { records })
        this.$emit('modal-closed')
      }).catch((err) => {
        if (err.response.status === 422) {
          if (err.response.data.errors && err.response.data.errors.file_header && err.response.data.errors.file_header.length > 0) {
            this.malformedExcelHeaderErrorFields = err.response.data.errors.file_header
          } if (err.response.data.errors && err.response.data.errors.validation_fail && err.response.data.errors.validation_fail.length > 0) {
            let missingProcessId = 0
            let missingDates = 0
            for (let elem of err.response.data.errors.validation_fail) {
              if (elem.attribute === "3") {
                missingProcessId += 1
              }
              if (elem.attribute === "4") {
                missingDates += 1
              }
            }
            if (missingProcessId > 0 && missingDates > 0) {
              this.excelValidationError = this.$t('ranu.records.errors.file_validation_fail_attributes.missing_process_id_missing_dates', {missing_process_count: missingProcessId, missing_dates_count: missingDates})
            }
            if (missingProcessId > 0 && missingDates === 0) {
              this.excelValidationError = this.$t('ranu.records.errors.file_validation_fail_attributes.missing_process_id', {missing_process_count: missingProcessId})
            }
            if (missingProcessId === 0 && missingDates > 0) {
              this.excelValidationError = this.$t('ranu.records.errors.file_validation_fail_attributes.missing_dates', {missing_dates_count: missingDates})
            }
            /*let str = ''
            for (let elem of err.response.data.errors.validation_fail) {
              str += `Linha: ${elem.row}, ${this.$t(`ranu.records.errors.file_validation_fail_attributes.${elem.attribute}`)};\n`
            }
            this.excelValidationError = this.$t('ranu.records.errors.file_validation_fail', { fields_str: str })*/
          } else {
            this.formErrors.record(err.response.data.errors, 'ranu.records.errors.')
          }
        } else {
          this.showToast(this.$t('ranu.records.errors.something_went_wrong'), this.$store.state.errorToastOptions)
        }
      }).finally(() => {
        this.isFetchingServer = false
        this.uploadProgress = 0
        // this.$refs.importRecords.value = ''
      })
    },
    cancel: function () {
      this.$emit('modal-closed')
    }
  },
  computed: {
    ranuService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ranu_server) || null
    },
    recordPermissions: function () {
      return this.ranuService.user_permissions.filter(permission => permission.access_type === 2 && permission.info_type === 100)
    },
    entities: function () {
      if (this.recordPermissions.some(permission => permission.entity_id === null)) {
        return this.ranuService.entities
      } else {
        return this.ranuService.entities.filter(entity => this.recordPermissions.some(permission => permission.entity_id === entity.id))
      }
    },
    formHasErrors: function () {
      return !this.form.entity || !this.form.file
    },
    writeEntities: function () {
      return this.entities.filter(entity => this.recordPermissions.some(permission => permission.entity_id === null || permission.entity_id === entity.id))
    }
  }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
.multi .multiselect__tag {
  background-color: #29c4a9;
}

.multi .multiselect__option--highlight {
  background-color: #29c4a9;
}

.multi .multiselect__option--highlight::after {
  background-color: #29c4a9;
}

.multi .multiselect__option--selected {
  background-color: #d9d9d9;
}

.multi .multiselect__option--selected.multiselect__option--highlight {
  background-color: #e34a4a;
}

.multi .multiselect__option--selected.multiselect__option--highlight::after {
  background-color: #e34a4a;
}

.multi .multiselect--disabled .multiselect__select {
  background: none;
}

label.multi__select__label {
  justify-content: normal;
  font-size: 0.6rem;
  color: #29c4a9;
  font-weight: 600;
  text-transform: uppercase;
  top: -0.6rem;
  left: 0;
  margin-bottom: 0;
}
</style>

<style scoped lang="scss">
i:hover {
  cursor: pointer;
}
</style>
